/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react'
import { FileInputButton } from '../../styles/styles'
import Modal from 'live/components/ui/Modal'
import Label from 'live/components/forms/ui/label'
import LiveSelect from 'live/components/forms/ui/select'
import ActionToggle from 'live/components/ui/action-toggle'
import TagsInput from 'react-tagsinput'
import i18n from 'live/services/i18n'

interface UploadModalProps {
    source: string
    showModal: boolean
    setShowModal: (show: boolean) => void
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    filePath: string
    formData: FormData | undefined
    header: string[]
    wellName: string
    setWellName: (value: string) => void
    rigName: string
    setRigName: (value: string) => void
    logName: string
    setLogName: (value: string) => void
    logType: string
    setLogType: (value: string) => void
    indexColumn: string
    setIndexColumn: (value: string) => void
    setIndexColumnNumber: (index: number) => void
    indexColumnNumber: number
    dataLine: string[]
    indexFormat: string
    setIndexFormat: (value: string) => void
    timezone: string
    setTimezone: (value: string) => void
    unitsOnHeader: boolean
    setUnitsOnHeader: (checked: boolean) => void
    loading: boolean
    uploadFile: (formData: FormData) => Promise<void>
    nullValues?: string[]
    setNullValues?: (value: ((prevState: string[]) => string[]) | string[]) => void
    sourceName?: string
    setSourceName?: (value: string) => void
    sourceKind?: string
    setSourceKind?: (value: string) => void
    protocolName?: string
    setProtocolName?: (value: string) => void
}

const UploadModal: React.FC<UploadModalProps> = ({
    source,
    showModal,
    setShowModal,
    handleFileChange,
    filePath,
    formData,
    header,
    wellName,
    setWellName,
    rigName,
    setRigName,
    logName,
    setLogName,
    logType,
    setLogType,
    indexColumn,
    setIndexColumn,
    setIndexColumnNumber,
    indexColumnNumber,
    dataLine,
    indexFormat,
    setIndexFormat,
    timezone,
    setTimezone,
    unitsOnHeader,
    setUnitsOnHeader,
    loading,
    uploadFile,
    nullValues,
    setNullValues,
    sourceName,
    setSourceName,
    sourceKind,
    setSourceKind,
    protocolName,
    setProtocolName
}: UploadModalProps) => {
    return (
        <Modal
            closable
            show={showModal}
            onHide={() => setShowModal(false)}
            onSave={async () => {
                await uploadFile(formData)
                setShowModal(false)
            }}
        >
            <Modal.Header>
                <Modal.Title>{i18n('Customize your upload')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <div className={'form-group'}>
                        <Label required>Choose File</Label>
                        <FileInputButton style={{ flex: 1 }}>
                            Choose CSV File
                            <input type="file" accept={'text/csv'} onChange={handleFileChange} />
                        </FileInputButton>
                    </div>
                    <label>{filePath}</label>
                    <br />
                    {source.startsWith('well') && (
                        <>
                            <div className={'form-group'}>
                                <Label required>{i18n('Well Name')}</Label>
                                <input
                                    type="text"
                                    className="liveInout"
                                    placeholder={'Enter Well Name'}
                                    value={wellName}
                                    onChange={({ target }) => setWellName(target.value)}
                                />
                            </div>
                            <div className={'form-group'}>
                                <Label required>{i18n('Rig Name')}</Label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={'Enter Rig Name'}
                                    value={rigName}
                                    onChange={({ target }) => setRigName(target.value)}
                                />
                            </div>
                            <div className={'form-group'}>
                                <Label required>{i18n('Log Name')}</Label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={'Enter Log Name (usually the file name)'}
                                    value={logName}
                                    onChange={({ target }) => setLogName(target.value)}
                                />
                            </div>
                            <div className={'form-group'}>
                                <Label required>{i18n('Log Type')}</Label>
                                <LiveSelect
                                    disable={formData === undefined}
                                    placeholder={'Select a type'}
                                    options={['date time', 'measured depth'].map(type => ({
                                        value: type,
                                        label: type
                                    }))}
                                    value={logType}
                                    onChange={(value: string) => setLogType(value)}
                                />
                            </div>
                            <br />
                        </>
                    )}
                    <div className={'form-group'}>
                        <Label required>Index Column</Label>
                        <LiveSelect
                            disable={formData === undefined}
                            placeholder={formData === undefined ? 'Select a file first' : 'Select a column'}
                            options={header.map(item => ({
                                value: item,
                                label: item
                            }))}
                            value={indexColumn}
                            onChange={(value: string) => {
                                setIndexColumn(value)
                                setIndexColumnNumber(header.indexOf(value))
                            }}
                        />
                    </div>
                    <div className={'form-group'}>
                        <Label required>
                            {i18n(
                                `Index Format ${indexColumnNumber >= 0 ? `(e.g: ${dataLine[indexColumnNumber]})` : ''}`
                            )}
                        </Label>
                        <LiveSelect
                            disable={formData === undefined}
                            placeholder={formData === undefined ? 'Select a file first' : 'Select a format'}
                            options={dateFormats.map(format => ({
                                value: format,
                                label: format
                            }))}
                            value={indexFormat}
                            onChange={(value: string) => setIndexFormat(value)}
                            searchable={true}
                        />
                    </div>
                    <div className={'form-group'}>
                        <Label required>Timezone</Label>
                        <LiveSelect
                            disable={formData === undefined}
                            placeholder={formData === undefined ? 'Select a file first' : 'Select a timezone'}
                            options={timezones.map(zone => ({
                                value: zone,
                                label: zone
                            }))}
                            value={timezone}
                            onChange={(value: string) => setTimezone(value)}
                            searchable={true}
                        />
                    </div>
                    <div className={'form-group'}>
                        <Label required>
                            Units on Header (valid separators: double underscore, dash, tab, (...) or [...])
                        </Label>
                        <ActionToggle
                            active={unitsOnHeader === undefined ? false : unitsOnHeader}
                            onChange={() => setUnitsOnHeader(!unitsOnHeader)}
                        />
                    </div>
                    <div>
                        <Label>Null Values (comma-separated)</Label>
                        <TagsInput
                            value={nullValues || []}
                            onChange={setNullValues}
                            addOnBlur
                            // @ts-ignore
                            placeholder={'Enter null values (e.g. -999.25, -999.0, -9999, -9999.0)'}
                        />
                    </div>
                    <br />
                    <details style={{ marginTop: '1rem' }}>
                        <summary>{i18n('+ Advanced Options')}</summary>
                        <div className={'form-group'}>
                            <Label>{i18n('Source Name')}</Label>
                            <input
                                type="text"
                                className="liveInout"
                                placeholder={
                                    'Enter an optional source name that will be mapped on liverig metadata field'
                                }
                                value={sourceName}
                                onChange={({ target }) => setSourceName(target.value)}
                            />
                        </div>
                        <div className={'form-group'}>
                            <Label>{i18n('Source Kind')}</Label>
                            <input
                                type="text"
                                className="liveInout"
                                placeholder={
                                    'Enter an optional source kind that will be mapped on liverig metadata field'
                                }
                                value={sourceKind}
                                onChange={({ target }) => setSourceKind(target.value)}
                            />
                        </div>
                        <div className={'form-group'}>
                            <Label>{i18n('Protocol Name')}</Label>
                            <input
                                type="text"
                                className="liveInout"
                                placeholder={
                                    'Enter an optional protocol name that will be mapped on liverig metadata field'
                                }
                                value={protocolName}
                                onChange={({ target }) => setProtocolName(target.value)}
                            />
                        </div>
                    </details>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <input
                    type="button"
                    className="btn btn-primary"
                    value={loading ? i18n('Uploading...') : i18n('Upload')}
                    disabled={
                        !formData ||
                        !indexColumn ||
                        !indexFormat ||
                        !timezone ||
                        loading ||
                        (source.startsWith('well') && (!wellName || !rigName || !logName || !logType))
                    }
                    onClick={async () => {
                        await uploadFile(formData)
                        setShowModal(false)
                    }}
                />
                <input
                    type={'button'}
                    className={'btn btn-default'}
                    value={i18n('Cancel')}
                    onClick={() => setShowModal(false)}
                />
            </Modal.Footer>
        </Modal>
    )
}

export default UploadModal

const dateFormats = [
    'yyyy-MM-dd HH:mm:ss',
    'MM/dd/yyyy',
    'dd-MM-yyyy',
    'yyyy/MM/dd',
    'dd MMM yyyy',
    'EEE, MMM d, yyyy',
    "yyyy.MM.dd G 'at' HH:mm:ss z",
    "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
    'YYYYMMDDhhmmss',
    'Guess Format'
]

const timezones = [
    'Pacific/Apia',
    'Pacific/Niue',
    'Pacific/Pago_Pago ',
    'America/Adak ',
    'Pacific/Fakaofo ',
    'Pacific/Honolulu ',
    'Pacific/Rarotonga ',
    'Pacific/Tahiti ',
    'Pacific/Marquesas ',
    'America/Anchorage',
    'Pacific/Gambier',
    'America/Los_Angeles',
    'America/Tijuana',
    'America/Vancouver',
    'Pacific/Pitcairn',
    'America/Dawson_Creek',
    'America/Denver',
    'America/Edmonton',
    'America/Mazatlan',
    'America/Phoenix',
    'America/Belize',
    'America/Chicago',
    'America/Costa_Rica',
    'America/El_Salvador',
    'America/Guatemala',
    'America/Managua',
    'America/Mexico_City',
    'America/Regina',
    'America/Tegucigalpa',
    'America/Winnipeg',
    'Pacific/Easter',
    'Pacific/Galapagos',
    'America/Bogota',
    'America/Cayman',
    'America/Grand_Turk',
    'America/Guayaquil',
    'America/Havana',
    'America/Indianapolis',
    'America/Jamaica',
    'America/Lima',
    'America/Montreal',
    'America/Nassau',
    'America/New_York',
    'America/Panama',
    'America/Port-au-Prince',
    'America/Porto_Acre',
    'America/Rio_Branco',
    'America/Anguilla',
    'America/Antigua',
    'America/Aruba',
    'America/Asuncion',
    'America/Barbados',
    'America/Caracas',
    'America/Cuiaba',
    'America/Curacao',
    'America/Dominica',
    'America/Grenada',
    'America/Guadeloupe',
    'America/Guyana',
    'America/Halifax',
    'America/La_Paz',
    'America/Manaus',
    'America/Martinique',
    'America/Montserrat',
    'America/Port_of_Spain',
    'America/Puerto_Rico',
    'America/Santiago',
    'America/Santo_Domingo',
    'America/St_Kitts',
    'America/St_Lucia',
    'America/St_Thomas',
    'America/St_Vincent',
    'America/Thule',
    'America/Tortola',
    'Antarctica/Palmer',
    'Atlantic/Bermuda',
    'Atlantic/Stanley',
    'America/St_Johns',
    'America/Buenos_Aires',
    'America/Cayenne',
    'America/Fortaleza',
    'America/Godthab',
    'America/Miquelon',
    'America/Montevideo',
    'America/Paramaribo',
    'America/Sao_Paulo',
    'America/Noronha',
    'Atlantic/South_Georgia',
    'America/Scoresbysund',
    'Atlantic/Azores',
    'Atlantic/Cape_Verde',
    'Atlantic/Jan_Mayen',
    'Africa/Abidjan',
    'Africa/Accra',
    'Africa/Banjul',
    'Africa/Bissau',
    'Africa/Casablanca',
    'Africa/Conakry',
    'Africa/Dakar',
    'Africa/Freetown',
    'Africa/Lome',
    'Africa/Monrovia',
    'Africa/Nouakchott',
    'Africa/Ouagadougou',
    'Africa/Sao_Tome',
    'Africa/Timbuktu',
    'Atlantic/Canary',
    'Atlantic/Faeroe',
    'Atlantic/Reykjavik',
    'Atlantic/St_Helena',
    'Europe/Dublin',
    'Europe/Lisbon',
    'Europe/London',
    'Africa/Algiers',
    'Africa/Bangui',
    'Africa/Douala',
    'Africa/Kinshasa',
    'Africa/Lagos',
    'Africa/Libreville',
    'Africa/Luanda',
    'Africa/Malabo',
    'Africa/Ndjamena',
    'Africa/Niamey',
    'Africa/Porto-Novo',
    'Africa/Tunis',
    'Africa/Windhoek',
    'Europe/Amsterdam',
    'Europe/Andorra',
    'Europe/Belgrade',
    'Europe/Berlin',
    'Europe/Brussels',
    'Europe/Budapest',
    'Europe/Copenhagen',
    'Europe/Gibraltar',
    'Europe/Luxembourg',
    'Europe/Madrid',
    'Europe/Malta',
    'Europe/Monaco',
    'Europe/Oslo',
    'Europe/Paris',
    'Europe/Prague',
    'Europe/Rome',
    'Europe/Stockholm',
    'Europe/Tirane',
    'Europe/Vaduz',
    'Europe/Vienna',
    'Europe/Warsaw',
    'Europe/Zurich',
    'Africa/Blantyre',
    'Africa/Bujumbura',
    'Africa/Cairo',
    'Africa/Gaborone',
    'Africa/Harare',
    'Africa/Johannesburg',
    'Africa/Kigali',
    'Africa/Lubumbashi',
    'Africa/Lusaka',
    'Africa/Maputo',
    'Africa/Maseru',
    'Africa/Mbabane',
    'Africa/Tripoli',
    'Asia/Amman',
    'Asia/Beirut',
    'Asia/Damascus',
    'Asia/Jerusalem',
    'Asia/Nicosia',
    'Europe/Athens',
    'Europe/Bucharest',
    'Europe/Chisinau',
    'Europe/Helsinki',
    'Europe/Istanbul',
    'Europe/Kaliningrad',
    'Europe/Kiev',
    'Europe/Minsk',
    'Europe/Riga',
    'Europe/Simferopol',
    'Europe/Sofia',
    'Europe/Tallinn',
    'Europe/Vilnius',
    'Africa/Addis_Ababa',
    'Africa/Asmera',
    'Africa/Dar_es_Salaam',
    'Africa/Djibouti',
    'Africa/Kampala',
    'Africa/Khartoum',
    'Africa/Mogadishu',
    'Africa/Nairobi',
    'Asia/Aden',
    'Asia/Baghdad',
    'Asia/Bahrain',
    'Asia/Kuwait',
    'Asia/Qatar',
    'Asia/Riyadh',
    'Europe/Moscow',
    'Indian/Antananarivo',
    'Indian/Comoro',
    'Indian/Mayotte',
    'Asia/Tehran',
    'Asia/Aqtau',
    'Asia/Baku',
    'Asia/Dubai',
    'Asia/Muscat',
    'Asia/Tbilisi',
    'Asia/Yerevan',
    'Europe/Samara',
    'Indian/Mahe',
    'Indian/Mauritius',
    'Indian/Reunion',
    'Asia/Kabul',
    'Asia/Aqtobe',
    'Asia/Ashgabat',
    'Asia/Ashkhabad',
    'Asia/Bishkek',
    'Asia/Dushanbe',
    'Asia/Karachi',
    'Asia/Tashkent',
    'Asia/Yekaterinburg',
    'Indian/Chagos',
    'Indian/Kerguelen',
    'Indian/Maldives',
    'Asia/Calcutta',
    'Asia/Katmandu',
    'Antarctica/Mawson',
    'Asia/Almaty',
    'Asia/Colombo',
    'Asia/Dacca',
    'Asia/Dhaka',
    'Asia/Novosibirsk',
    'Asia/Thimbu',
    'Asia/Thimphu',
    'Asia/Rangoon',
    'Indian/Cocos',
    'Asia/Bangkok',
    'Asia/Jakarta',
    'Asia/Krasnoyarsk',
    'Asia/Phnom_Penh',
    'Asia/Saigon',
    'Asia/Vientiane',
    'Indian/Christmas',
    'Antarctica/Casey',
    'Asia/Brunei',
    'Asia/Hong_Kong',
    'Asia/Irkutsk',
    'Asia/Kuala_Lumpur',
    'Asia/Macao',
    'Asia/Manila',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Taipei',
    'Asia/Ujung_Pandang',
    'Asia/Ulaanbaatar',
    'Asia/Ulan_Bator',
    'Australia/Perth',
    'Asia/Jayapura',
    'Asia/Pyongyang',
    'Asia/Seoul',
    'Asia/Tokyo',
    'Asia/Yakutsk',
    'Pacific/Palau',
    'Australia/Adelaide',
    'Australia/Broken_Hill',
    'Australia/Darwin',
    'Antarctica/DumontDUrville',
    'Asia/Vladivostok',
    'Australia/Brisbane',
    'Australia/Hobart',
    'Australia/Sydney',
    'Pacific/Guam',
    'Pacific/Port_Moresby',
    'Pacific/Saipan',
    'Pacific/Truk',
    'Australia/Lord_Howe',
    'Asia/Magadan',
    'Pacific/Efate',
    'Pacific/Guadalcanal',
    'Pacific/Kosrae',
    'Pacific/Noumea',
    'Pacific/Ponape',
    'Pacific/Norfolk',
    'Antarctica/McMurdo',
    'Asia/Anadyr',
    'Asia/Kamchatka',
    'Pacific/Auckland',
    'Pacific/Fiji',
    'Pacific/Funafuti',
    'Pacific/Majuro',
    'Pacific/Nauru',
    'Pacific/Tarawa',
    'Pacific/Wake',
    'Pacific/Wallis',
    'Pacific/Chatham',
    'Pacific/Enderbury',
    'Pacific/Tongatapu',
    'Pacific/Kiritimati'
]
